<!-- src/components/modal/estimate-preview-modal.vue -->
<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import * as bootstrap from "bootstrap";
import { useEstimateStore } from "@/stores/estimateStore";
import { useCompanyStore } from "@/stores/companyStore";

defineOptions({ name: "EstimatePreviewModal" });

const estimateStore = useEstimateStore();
const companyStore = useCompanyStore();

const modalRef = ref(null);
let modalInstance = null;

const loading = ref(false);
const estimate = ref(null);

/** Base URL for resolving /storage paths (your env already has this) */
const BASE_URL =
  (import.meta?.env?.VITE_BASE_URL || process.env.VUE_APP_BASE_URL || "").trim();

/** Safely pick active company from store */
const company = computed(() => {
  const activeId =
    companyStore.activeCompanyId?.value ?? companyStore.activeCompanyId;

  const list = companyStore.companies?.value ?? companyStore.companies ?? [];
  return (list || []).find((x) => x.id === activeId) || {};
});

/** Resolve logo URL (handles absolute urls, data urls, and /storage relative urls) */
function resolveUrl(path) {
  if (!path) return "";
  const s = String(path);

  // already absolute or data url
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:"))
    return s;

  // normalize base url
  const base = BASE_URL ? BASE_URL.replace(/\/+$/, "") : "";
  if (!base) return s; // fallback

  // ensure leading slash
  const p = s.startsWith("/") ? s : `/${s}`;
  return `${base}${p}`;
}

function toNumber(v) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(v) {
  const num = toNumber(v);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(v) {
  if (!v) return "-";
  // if backend gives YYYY-MM-DD, keep stable
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

const items = computed(() => estimate.value?.items || []);

const subTotal = computed(() =>
  items.value.reduce((sum, it) => {
    const qty = toNumber(it.qty ?? it.quantity ?? 1);
    const unit = toNumber(it.unit_cost ?? it.rate ?? 0);
    return sum + unit * qty;
  }, 0)
);

const discountAmount = computed(() => {
  // support two styles:
  // 1) estimate.discount_total (money)
  // 2) estimate.discount_percent (percent)
  const fixed = toNumber(estimate.value?.discount_total);
  if (fixed) return fixed;

  const percent = toNumber(estimate.value?.discount_percent);
  if (!percent) return 0;
  return (subTotal.value * percent) / 100;
});

const taxAmount = computed(() => {
  // support two styles:
  // 1) estimate.tax_total (money)
  // 2) estimate.tax_rate (percent)
  const fixed = toNumber(estimate.value?.tax_total);
  if (fixed) return fixed;

  const rate = toNumber(estimate.value?.tax_rate);
  if (!rate) return 0;

  // common practice: tax on (sub - discount)
  const taxable = Math.max(0, subTotal.value - discountAmount.value);
  return (taxable * rate) / 100;
});

const grandTotal = computed(() => {
  const api = toNumber(estimate.value?.grand_total);
  if (api) return api;
  return subTotal.value - discountAmount.value + taxAmount.value;
});

function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "accepted":
      return "dq-pill dq-pill-success";
    case "sent":
      return "dq-pill dq-pill-primary";
    case "expired":
      return "dq-pill dq-pill-warning";
    case "declined":
    case "rejected":
      return "dq-pill dq-pill-danger";
    case "draft":
      return "dq-pill dq-pill-slate";
    default:
      return "dq-pill dq-pill-slate";
  }
}

async function open(row) {
  if (!row?.id) return;

  loading.value = true;
  estimate.value = null;

  try {
    // ensure companies exist (so logo/name show)
    if (!((companyStore.companies?.value ?? companyStore.companies)?.length)) {
      await companyStore.fetchCompanies?.();
    }

    const data = await estimateStore.fetchOne(row.id);
    estimate.value = data || estimateStore.current || row;

    await nextTick();
    modalInstance?.show();
  } catch (e) {
    console.error("Failed to load estimate preview", e);
  } finally {
    loading.value = false;
  }
}

defineExpose({ open });

onMounted(() => {
  if (modalRef.value) {
    modalInstance = new bootstrap.Modal(modalRef.value, {
      backdrop: true,
      keyboard: true,
      focus: true,
    });
  }
});
</script>

<template>
  <div class="modal fade" id="estimate_preview_modal" tabindex="-1" ref="modalRef">
    <!-- ✅ NO modal-dialog-centered (it breaks height/scroll in many layouts) -->
    <div class="modal-dialog modal-xl dq-dialog">
      <div class="modal-content dq-modal">
        <!-- Header -->
        <div class="modal-header dq-header">
          <div class="dq-hleft">
            <div class="dq-brand">
              <div class="dq-logo">
                <img
                  v-if="company?.logo_url"
                  :src="resolveUrl(company.logo_url)"
                  alt="logo"
                />
                <i v-else class="ti ti-receipt-2"></i>
              </div>

              <div class="dq-titlewrap">
                <div class="dq-title">Estimate Preview</div>
                <div class="dq-sub">
                  {{ company?.company_name || "Company" }}
                  <span class="dq-dot">•</span>
                  <span class="dq-muted">Quick review before sending</span>
                </div>
              </div>
            </div>

            <div v-if="estimate" class="dq-meta">
              <span class="dq-k">Estimate No</span>
              <span class="dq-v">#{{ estimate.estimate_no || "EST-XXX" }}</span>
              <span class="dq-dot">•</span>
              <span class="dq-k">Status</span>
              <span :class="statusClass(estimate.status)">
                {{ estimate.status || "—" }}
              </span>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <!-- ✅ Scroll container -->
        <div class="modal-body dq-body">
          <!-- Loading -->
          <div v-if="loading" class="dq-loading">
            <span class="spinner-border text-primary" role="status" aria-hidden="true" />
            <div class="dq-loading-text">Loading estimate…</div>
          </div>

          <div v-else-if="estimate" class="dq-wrap">
            <div class="dq-grid">
              <!-- LEFT -->
              <div class="dq-left">
                <!-- Top info -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-file-text"></i>
                      <span>Estimate Details</span>
                    </div>
                    <div class="dq-dates">
                      <div class="dq-date">
                        <span class="dq-k">Estimate</span>
                        <span class="dq-v">{{ formatDate(estimate.estimate_date) }}</span>
                      </div>
                      <div class="dq-date">
                        <span class="dq-k">Expiry</span>
                        <span class="dq-v">{{ formatDate(estimate.expiry_date) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-fromto">
                      <div class="dq-box">
                        <div class="dq-box-h">From</div>
                        <div class="dq-box-title">
                          {{ company.company_name || "Company Name" }}
                        </div>
                        <div class="dq-box-line">
                          <i class="ti ti-map-pin"></i>
                          <span>{{ company.address || "Company address" }}</span>
                        </div>
                        <div class="dq-box-line">
                          <i class="ti ti-mail"></i>
                          <span>{{ company.email || "company@example.com" }}</span>
                        </div>
                        <div class="dq-box-line">
                          <i class="ti ti-phone"></i>
                          <span>{{ company.phone || "+000 000 000" }}</span>
                        </div>
                      </div>

                      <div class="dq-box">
                        <div class="dq-box-h">To</div>
                        <div class="dq-box-title">
                          {{ estimate.client?.name || "Client Name" }}
                        </div>
                        <div class="dq-box-line">
                          <i class="ti ti-map-pin"></i>
                          <span>{{ estimate.client_address || "Client address" }}</span>
                        </div>
                        <div class="dq-box-line">
                          <i class="ti ti-mail"></i>
                          <span>{{ estimate.email || estimate.client?.email || "—" }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="dq-note" v-if="estimate.title">
                      <span class="dq-k">Estimate For</span>
                      <span class="dq-v">{{ estimate.title }}</span>
                    </div>
                  </div>
                </div>

                <!-- Items -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <i class="ti ti-shopping-cart"></i>
                      <span>Items</span>
                    </div>
                    <div class="dq-muted small">
                      {{ items.length }} line{{ items.length === 1 ? "" : "s" }}
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="table-responsive dq-table">
                      <table class="table mb-0">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Description</th>
                            <th class="text-end">Qty</th>
                            <th class="text-end">Unit Cost</th>
                            <th class="text-end">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(it, idx) in items" :key="idx">
                            <td class="dq-td-strong">
                              {{ it.item || "—" }}
                            </td>
                            <td class="dq-td-muted">
                              {{ it.description || "—" }}
                            </td>
                            <td class="text-end">
                              {{ it.qty ?? it.quantity ?? 1 }}
                            </td>
                            <td class="text-end">
                              {{ formatMoney(it.unit_cost ?? it.rate) }}
                            </td>
                            <td class="text-end dq-td-strong">
                              {{
                                formatMoney(
                                  toNumber(it.unit_cost ?? it.rate) *
                                    toNumber(it.qty ?? it.quantity ?? 1)
                                )
                              }}
                            </td>
                          </tr>

                          <tr v-if="!items.length">
                            <td colspan="5" class="text-center text-muted py-4">
                              No items found.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div v-if="estimate.other_information" class="dq-other mt-3">
                      <div class="dq-other-h">Other Information</div>
                      <div class="dq-other-t">{{ estimate.other_information }}</div>
                    </div>
                  </div>
                </div>

                <!-- Footer note -->
                <div class="dq-footnote">
                  This is an estimate only. Final invoice may vary based on actual work performed
                  or changes in scope.
                </div>
              </div>

              <!-- RIGHT (Sticky totals on desktop) -->
              <div class="dq-right">
                <div class="dq-sticky">
                  <div class="dq-card">
                    <div class="dq-card-head">
                      <div class="dq-card-title">
                        <i class="ti ti-calculator"></i>
                        <span>Summary</span>
                      </div>
                    </div>

                    <div class="dq-card-body">
                      <div class="dq-row">
                        <span class="dq-k">Sub Total</span>
                        <span class="dq-v">{{ formatMoney(subTotal) }}</span>
                      </div>

                      <div class="dq-row">
                        <span class="dq-k">Discount</span>
                        <span class="dq-v">- {{ formatMoney(discountAmount) }}</span>
                      </div>

                      <div class="dq-row" v-if="toNumber(estimate.tax_rate) || toNumber(estimate.tax_total)">
                        <span class="dq-k">
                          Tax
                          <span v-if="toNumber(estimate.tax_rate)">
                            ({{ toNumber(estimate.tax_rate) }}%)
                          </span>
                        </span>
                        <span class="dq-v">{{ formatMoney(taxAmount) }}</span>
                      </div>

                      <div class="dq-divider"></div>

                      <div class="dq-row dq-total">
                        <span>Total</span>
                        <span>{{ formatMoney(grandTotal) }}</span>
                      </div>

                      <button
                        type="button"
                        class="btn btn-light w-100 mt-3"
                        data-bs-dismiss="modal"
                      >
                        <i class="ti ti-x me-2"></i>Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="py-4 text-center text-muted">
            No estimate selected.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ---------- scrollable modal foundation ---------- */
.dq-dialog {
  height: calc(100vh - 2rem);
  margin: 1rem auto;
}
.dq-modal {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 30px 80px rgba(16, 24, 40, 0.18);
}
.dq-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: rgba(248, 250, 252, 0.55);
  -webkit-overflow-scrolling: touch;
}

/* ---------- header ---------- */
.dq-header {
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-hleft {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.dq-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-logo {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  background: rgba(37, 99, 235, 0.1);
  color: rgba(37, 99, 235, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.dq-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dq-titlewrap {
  line-height: 1.1;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.02em;
  color: rgba(17, 24, 39, 0.92);
  font-size: 1.05rem;
}
.dq-sub {
  font-size: 0.86rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}
.dq-dot {
  margin: 0 8px;
  opacity: 0.5;
}
.dq-muted {
  color: rgba(17, 24, 39, 0.55);
}
.dq-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.dq-k {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.6);
}
.dq-v {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}

/* pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.78rem;
  text-transform: capitalize;
}
.dq-pill-primary {
  background: rgba(37, 99, 235, 0.1);
  color: rgba(37, 99, 235, 1);
}
.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-pill-warning {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}
.dq-pill-danger {
  background: rgba(220, 38, 38, 0.12);
  color: rgba(220, 38, 38, 1);
}
.dq-pill-slate {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(51, 65, 85, 1);
}

/* ---------- layout ---------- */
.dq-wrap {
  padding: 16px;
}
.dq-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 14px;
}
@media (max-width: 992px) {
  .dq-grid {
    grid-template-columns: 1fr;
  }
}

.dq-sticky {
  position: sticky;
  top: 12px;
}
@media (max-width: 992px) {
  .dq-sticky {
    position: static;
  }
}

/* ---------- cards ---------- */
.dq-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  margin-bottom: 14px;
}
.dq-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}
.dq-card-body {
  padding: 14px 16px;
}

/* details */
.dq-dates {
  display: flex;
  gap: 12px;
  align-items: center;
}
.dq-date {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
}
.dq-fromto {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 768px) {
  .dq-fromto {
    grid-template-columns: 1fr;
  }
}
.dq-box {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 12px;
  background: rgba(248, 250, 252, 0.6);
}
.dq-box-h {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.7);
  margin-bottom: 6px;
}
.dq-box-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
  margin-bottom: 8px;
}
.dq-box-line {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: rgba(17, 24, 39, 0.75);
  margin-bottom: 6px;
}
.dq-box-line i {
  opacity: 0.7;
  margin-top: 2px;
}
.dq-note {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(37, 99, 235, 0.06);
  border: 1px dashed rgba(37, 99, 235, 0.25);
}

/* table */
.dq-table thead th {
  font-size: 0.78rem;
  font-weight: 950;
  color: rgba(17, 24, 39, 0.55);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.dq-table tbody td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
  vertical-align: top;
}
.dq-td-strong {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}
.dq-td-muted {
  color: rgba(17, 24, 39, 0.7);
}

/* other info */
.dq-other {
  border-radius: 16px;
  padding: 12px;
  background: rgba(248, 250, 252, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-other-h {
  font-weight: 950;
  margin-bottom: 6px;
  color: rgba(17, 24, 39, 0.9);
}
.dq-other-t {
  color: rgba(17, 24, 39, 0.7);
}

/* summary rows */
.dq-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}
.dq-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 10px 0;
}
.dq-total {
  font-size: 1.05rem;
  font-weight: 950;
}

/* loading */
.dq-loading {
  min-height: 320px;
  display: grid;
  place-items: center;
  gap: 10px;
}
.dq-loading-text {
  color: rgba(17, 24, 39, 0.6);
  font-weight: 800;
}

.dq-footnote {
  text-align: center;
  color: rgba(17, 24, 39, 0.6);
  font-size: 0.9rem;
  margin-top: 6px;
}
</style>
